import React, { useEffect, useRef, useState } from 'react';
import { socketService } from '@/services/SocketService';
import { inputCls } from './agentShared';
import { uid } from './agentShared';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { cn } from '@/utils/cn';

interface TestMsg {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
  error?: boolean;
}

interface TestPanelProps {
  agentId: string;
  agentName?: string;
}

const TestPanel: React.FC<TestPanelProps> = ({ agentId, agentName }) => {
  const [msgs, setMsgs] = useState<TestMsg[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(socketService.isConnected);
  const convId = useRef<string>(`test_${agentId}_${uid()}`);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);
  useEffect(() => { convId.current = `test_${agentId}_${uid()}`; setMsgs([]); }, [agentId]);

  useEffect(() => {
    const onChunk = (raw: unknown) => {
      const d = raw as { message_id: string; text?: string; conversation_id?: string };
      if (!d?.message_id || d.conversation_id !== convId.current) return;
      setMsgs((prev) => {
        const i = prev.findIndex((m) => m.id === d.message_id);
        if (i === -1) return [...prev, { id: d.message_id, role: 'assistant', text: d.text || '', streaming: true }];
        const c = [...prev];
        c[i] = { ...c[i], text: c[i].text + (d.text || '') };
        return c;
      });
    };
    const onDone = (raw: unknown) => {
      const d = raw as { message_id: string; conversation_id?: string };
      if (d?.conversation_id && d.conversation_id !== convId.current) return;
      setMsgs((prev) => prev.map((m) => (m.id === d?.message_id ? { ...m, streaming: false } : m)));
    };
    const onErr = (raw: unknown) => {
      const d = raw as { message?: string };
      setMsgs((prev) => [
        ...prev,
        { id: `err-${uid()}`, role: 'assistant', text: d?.message || 'Something went wrong.', error: true },
      ]);
    };
    const onConn = () => setConnected(true);
    const onDisc = () => setConnected(false);

    socketService.on('agent:response', onChunk);
    socketService.on('agent:response_done', onDone);
    socketService.on('agent:error', onErr);
    socketService.on('connect', onConn);
    socketService.on('disconnect', onDisc);
    return () => {
      socketService.off('agent:response', onChunk);
      socketService.off('agent:response_done', onDone);
      socketService.off('agent:error', onErr);
      socketService.off('connect', onConn);
      socketService.off('disconnect', onDisc);
    };
  }, []);

  const anyStreaming = msgs.some((m) => m.streaming);

  const send = () => {
    const text = input.trim();
    if (!text || anyStreaming) return;
    if (!socketService.isConnected) {
      setMsgs((prev) => [
        ...prev,
        { id: `err-${uid()}`, role: 'assistant', text: 'SAM is offline — the realtime gateway is not reachable.', error: true },
      ]);
      return;
    }
    setInput('');
    setMsgs((prev) => [...prev, { id: `u-${uid()}`, role: 'user', text }]);
    socketService.emit('message', {
      type: 'sam.message',
      assistant_id: agentId,
      message: text,
      conversation_id: convId.current,
      interaction_mode: 'text',
    });
  };

  const reset = () => {
    convId.current = `test_${agentId}_${uid()}`;
    setMsgs([]);
  };

  return (
    <div className="card-surface mx-auto flex h-[480px] max-w-2xl flex-col">
      <div className="flex items-center gap-2 border-b border-ui-border px-4 py-2.5 text-xs text-ui-text2">
        <span className={cn('h-1.5 w-1.5 rounded-full', connected ? 'bg-success' : 'bg-danger')} />
        {connected ? 'Live test session' : 'Offline'} · runs the saved {agentName || 'agent'}
        <span className="flex-1" />
        <Button type="button" variant="ghost" size="xs" onClick={reset}>Reset</Button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {msgs.length === 0 && (
          <EmptyState title="Send a message to test how this agent responds. Save your changes first — the test runs the saved agent." />
        )}
        {msgs.map((m) => (
          <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[80%] whitespace-pre-wrap rounded-card px-3.5 py-2 text-sm',
                m.role === 'user'
                  ? 'bg-accent-fill text-on-accent'
                  : m.error
                  ? 'bg-danger/10 text-danger-text'
                  : 'bg-ui-subtle text-ui-text',
              )}
            >
              {m.text}
              {m.streaming && (
                <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-steel align-middle" />
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-2 border-t border-ui-border p-3">
        <input
          className={inputCls}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Message the agent…"
        />
        <Button type="button" variant="accent" onClick={send} disabled={anyStreaming}>
          Send
        </Button>
      </div>
    </div>
  );
};

export default TestPanel;
